import { computed, ComputedRef, onBeforeUnmount, ref, watch } from 'vue';
import { RouteLocationNormalizedLoaded, useRoute } from 'vue-router';

import { getRecorderEmails, TV_WINDOW_NAME } from '@/constants';
import { getValueFromLocalstore, saveValueToLocalstore } from '@/services/localStorage.service';
import { UserRole } from '@/services/user.service';
import { useStore } from '@/store';
import { claimEventTab, EventTabClaim, EventTabStatus } from '@/utils/eventTabLimit';

export type TabLimitScreen = 'warning' | 'blocked' | null;

type SignedInUser = { event_role?: number; email?: string } | null | undefined;

function isRecorder(user: SignedInUser) {
  if (!user) {
    return false;
  }
  return user.event_role === UserRole.RECORDER_BOT || getRecorderEmails().includes(user.email ?? '');
}

function isLiveStreamTvPopup(route: RouteLocationNormalizedLoaded) {
  return route.query.tv_mode === 'true' && window.name === TV_WINDOW_NAME;
}

function eventIdOfEventPage(route: RouteLocationNormalizedLoaded) {
  const isEventPage = route.name === 'EventSplash' || route.path.startsWith('/e/');
  if (!isEventPage || !route.params.eventID) {
    return null;
  }
  return String(route.params.eventID);
}

function sharedFlagKey(eventId: string) {
  return `gc-event-tab-limit-flag:${eventId}`;
}

function readFlagSharedByOtherTabs(eventId: string): boolean | null {
  const value = getValueFromLocalstore(sharedFlagKey(eventId), null);
  return typeof value === 'boolean' ? value : null;
}

function shareFlagWithOtherTabs(eventId: string, enabled: boolean) {
  saveValueToLocalstore(sharedFlagKey(eventId), enabled);
}

export function useEventTabLimit(): {
  tabLimitScreen: ComputedRef<TabLimitScreen>;
  showEventPage: ComputedRef<boolean>;
  keepBothTabsOpen: () => void;
} {
  const route = useRoute();
  const store = useStore();

  const tabStatus = ref<EventTabStatus | null>(null);
  const userKeptBothTabsOpen = ref(false);
  const flagHeardByOtherTabs = ref<boolean | null>(null);

  const limitedEventId = computed(() => {
    if (isRecorder(store.getters.getCurrentUser) || isLiveStreamTvPopup(route)) {
      return null;
    }
    return eventIdOfEventPage(route);
  });

  let claim: EventTabClaim | null = null;

  function startClaimFor(eventId: string | null) {
    claim?.release();
    claim = null;
    tabStatus.value = null;
    userKeptBothTabsOpen.value = false;
    flagHeardByOtherTabs.value = null;

    if (!eventId) {
      return;
    }
    flagHeardByOtherTabs.value = readFlagSharedByOtherTabs(eventId);
    claim = claimEventTab(eventId, status => {
      tabStatus.value = status;
    });
  }

  watch(limitedEventId, startClaimFor, { immediate: true });
  onBeforeUnmount(() => claim?.release());

  const flagHeardByThisTab = computed<boolean | null>(() => store.state.event.enableSessionLimit);

  watch(
    [limitedEventId, flagHeardByThisTab],
    ([eventId, enabled]) => {
      if (eventId && enabled !== null) {
        shareFlagWithOtherTabs(eventId, enabled);
      }
    },
    { immediate: true }
  );

  const sessionLimitIsOn = computed(() => {
    if (flagHeardByThisTab.value !== null) {
      return flagHeardByThisTab.value;
    }
    return flagHeardByOtherTabs.value === true;
  });

  const tabLimitScreen = computed<TabLimitScreen>(() => {
    if (!sessionLimitIsOn.value) {
      return null;
    }
    if (tabStatus.value === 'blocked') {
      return 'blocked';
    }
    if (tabStatus.value === 'secondary' && !userKeptBothTabsOpen.value) {
      return 'warning';
    }
    return null;
  });

  const showEventPage = computed(() => {
    if (!limitedEventId.value) {
      return true;
    }
    if (tabStatus.value === null) {
      return false;
    }
    return tabLimitScreen.value === null;
  });

  function keepBothTabsOpen() {
    userKeptBothTabsOpen.value = true;
  }

  return { tabLimitScreen, showEventPage, keepBothTabsOpen };
}
